import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import c from 'classnames';

import { ImageFallback } from 'components';
import VideoResource from 'types/VideoResource';
import bgPlaceholder from 'assets/images/microbe-paper.svg';

import { getYouTubeThumbnail } from '../VideoResources.helpers';
import styles from './UpsellCarousel.module.css';

const AUTO_ADVANCE_MS = 3500;
const SETTLE_MS = 120; // debounce before we treat scrolling as finished
// Pad short lists up to this many slides so there are always a couple of cards
// of buffer on each side of center — a drag travels through them while the
// settle-rotation catches up, instead of hitting an edge right away.
const MIN_SLIDES = 5;

// A slide carries its own stable key, independent of the video it shows, so a
// padded list can repeat a video without colliding keys — and so rotation moves
// the DOM nodes (no remount / image reload) rather than recreating them.
interface Slide {
  key: number;
  video: VideoResource;
}

// Repeat the videos up to MIN_SLIDES, but only to a whole multiple of the source
// length, so the wrap-around seam never lands two identical thumbnails together.
const buildSlides = (videos: VideoResource[]): Slide[] => {
  const distinct = videos.length;
  if (distinct <= 1) return videos.map((video, key) => ({ key, video }));
  const total =
    distinct >= MIN_SLIDES
      ? distinct
      : Math.ceil(MIN_SLIDES / distinct) * distinct;
  return Array.from({ length: total }, (_, key) => ({
    key,
    video: videos[key % distinct],
  }));
};

// Rotate left by n (negative = right); always returns a fresh array.
const rotate = (slides: Slide[], n: number) => {
  const { length } = slides;
  const k = ((n % length) + length) % length;
  return [...slides.slice(k), ...slides.slice(0, k)];
};

interface UpsellCarouselProps {
  videos: VideoResource[];
}

// Centered "peek" carousel of exclusive thumbnails. Infinite by rotation, not by
// growing scroll: one card stays anchored at the array's center, and whenever
// scrolling settles (auto-advance OR a touch drag), we rotate the list so the
// card that landed at center becomes the anchor again and reset scroll to it.
// The reset is invisible — that card doesn't move pixel-wise; only cards that
// wrapped past the edge teleport, and they're off-screen when they do.
export const UpsellCarousel = ({ videos }: UpsellCarouselProps) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const settleRef = useRef<ReturnType<typeof setTimeout>>();
  // Re-center on the next paint (set on mount, on source change, and after a rotation).
  const pendingRef = useRef(true);
  const [order, setOrder] = useState(() => buildSlides(videos));
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const isLooping = videos.length > 1;
  const anchorIndex = isLooping ? Math.floor(order.length / 2) : 0;

  useEffect(() => {
    setOrder(buildSlides(videos));
    pendingRef.current = true;
  }, [videos]);

  const centerOn = (index: number) => {
    const track = trackRef.current;
    const child = track?.children[index] as HTMLElement | undefined;
    if (!track || !child) return;
    track.scrollLeft =
      child.offsetLeft - (track.clientWidth - child.clientWidth) / 2;
  };

  const nearestIndex = () => {
    const track = trackRef.current;
    if (!track) return anchorIndex;
    const center = track.scrollLeft + track.clientWidth / 2;
    let nearest = 0;
    let best = Infinity;
    Array.from(track.children).forEach((child, index) => {
      const el = child as HTMLElement;
      const distance = Math.abs(el.offsetLeft + el.clientWidth / 2 - center);
      if (distance < best) {
        best = distance;
        nearest = index;
      }
    });
    return nearest;
  };

  // Rotation + scroll reset must land in the same paint as the DOM reorder, so
  // the centered card stays put visually — hence useLayoutEffect, not useEffect.
  useLayoutEffect(() => {
    if (!isLooping || !pendingRef.current) return;
    pendingRef.current = false;
    centerOn(anchorIndex);
    setActiveIndex(anchorIndex);
  }, [order, isLooping, anchorIndex]);

  useEffect(() => () => clearTimeout(settleRef.current), []);

  useEffect(() => {
    if (isPaused || !isLooping) return;
    const id = setInterval(() => {
      const track = trackRef.current;
      if (!track) return;
      const slideWidth =
        (track.children[0] as HTMLElement | undefined)?.clientWidth ??
        track.clientWidth;
      track.scrollBy({ left: slideWidth, behavior: 'smooth' });
    }, AUTO_ADVANCE_MS);
    return () => clearInterval(id);
  }, [isPaused, isLooping]);

  if (!videos.length) return null;

  // Pull the settled card back to the anchor by rotating the list under it.
  // activeIndex moves to the anchor in the same batch as the rotation: the
  // settled card stays the active one, just relabeled — otherwise it would dim
  // and a neighbour would flash active for the one render before re-centering.
  const normalize = () => {
    if (!isLooping) return;
    const shift = nearestIndex() - anchorIndex;
    if (shift === 0) return;
    pendingRef.current = true;
    setOrder(prev => rotate(prev, shift));
    setActiveIndex(anchorIndex);
  };

  const handleScroll = () => {
    setActiveIndex(nearestIndex()); // live dim follows the finger / animation
    clearTimeout(settleRef.current);
    settleRef.current = setTimeout(normalize, SETTLE_MS);
  };

  return (
    <div
      className={styles.carousel}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={() => setIsPaused(true)}
      onTouchEnd={() => setIsPaused(false)}
    >
      <div className={styles.track} ref={trackRef} onScroll={handleScroll}>
        {order.map((slide, index) => (
          <div
            key={slide.key}
            className={c(styles.slide, index !== activeIndex && styles.dimmed)}
          >
            <ImageFallback
              src={slide.video.thumbnail_url}
              fallback={
                getYouTubeThumbnail(slide.video.video_url) || bgPlaceholder
              }
              alt={slide.video.full_title}
            />
          </div>
        ))}
      </div>
    </div>
  );
};
